import * as React from 'react';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import {
  ColumnDefinition,
  getFilters2,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { DATE_INTERVAL, FIRST_PAGE, LIMIT, SORT } from 'Src/consts/table';
import { FilterRow2 } from 'Src/components/Grid/FilterRow2';
import { GridFilterButtons } from 'Src/components/Grid/GridFilterButtons';
import { SelectOptions } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ColumnToggleButton } from 'Src/components/ColumnToggleButton/ColumnToggleButton';
import { Panel } from 'Src/components/TwoPanelEditor/Panel';
import { GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import {
  GeneralMessageRecipientPreviewItem,
  GeneralMessageRecipientType,
  getGeneralMessageSelectedRecipientsPreview,
  searchGeneralMessageRecipients,
  searchGeneralMessageSelectedRecipients
} from 'Src/helpers/service/admin/generalMessage';
import {
  GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE,
  GeneralMessageRecipientSelectionStateGroup,
  addGeneralMessageRecipientSelectionItems,
  getGeneralMessageRecipientPreviewItemKey,
  getGeneralMessageRecipientSelectionGroupSize,
  getGeneralMessageRecipientSelectionItemFromPreview,
  isGeneralMessageRecipientPreviewItemSelected,
  selectAllFilteredGeneralMessageRecipients,
  selectGeneralMessageRecipientPageItems,
  unselectGeneralMessageRecipientPageItems
} from 'Src/helpers/generalMessageRecipientSelection/generalMessageRecipientSelection';
import './GeneralMessageRecipientsSelector.scss';

interface Props {
  user: AppUser;
  recipientType: GeneralMessageRecipientType;
  columns: ColumnDefinition[];
  selectionGroup: GeneralMessageRecipientSelectionStateGroup;
  onSelectionGroupChange: (group: GeneralMessageRecipientSelectionStateGroup) => void;
  title?: string;
  availableTitle?: string;
  selectedTitle?: string;
  filterOptions?: SelectOptions;
  serverFieldOptions?: any;
  forms?: SchoolForm[];
  houses?: SchoolHouse[];
  onHelpClick?: () => void;
  helpLabel?: string;
}

function getRecipientName(item: GeneralMessageRecipientPreviewItem): string {
  const firstName = item.firstName || '';
  const lastName = item.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || item.userId;
}

function findReferenceItem(items: any[], ids: any[]): any {
  const normalizedIds = (ids || [])
    .filter(id => typeof id !== 'undefined' && id !== null && id !== '')
    .map(id => String(id));

  return (items || []).find(item => {
    const itemId = propz.get(item, ['id']) || propz.get(item, ['_id']);
    return typeof itemId !== 'undefined' && normalizedIds.indexOf(String(itemId)) !== -1;
  });
}

function getStudentTransferMeta(
  item: GeneralMessageRecipientPreviewItem,
  referenceForms: SchoolForm[],
  referenceHouses: SchoolHouse[]
): string {
  const birthday = item.birthday ? new Date(item.birthday).toLocaleDateString('en-GB') : '';
  const gender = item.gender ? GENDER_SERVER_TO_CLIENT_MAPPING[item.gender] || item.gender : '';
  const forms = propz.get<any[]>(item, ['forms'], []) || [];
  const houses = propz.get<any[]>(item, ['houses'], []) || [];
  const resolvedForm = findReferenceItem(referenceForms, [item.formId, ...(item.formIds || [])]);
  const resolvedHouse = findReferenceItem(referenceHouses, [item.houseId, ...(item.houseIds || [])]);
  const formName =
    getNamesFromList(forms) || getNameFromObject(propz.get(item, ['form'])) || getNameFromObject(resolvedForm);
  const houseName =
    getNamesFromList(houses) || getNameFromObject(propz.get(item, ['house'])) || getNameFromObject(resolvedHouse);
  const yearGroup = [
    item.yearGroup,
    propz.get(item, ['form', 'age']),
    propz.get(item, ['form', 'ageGroup']),
    propz.get(forms, [0, 'age']),
    propz.get(forms, [0, 'ageGroup']),
    propz.get(resolvedForm, ['age']),
    propz.get(resolvedForm, ['ageGroup'])
  ].find(value => typeof value !== 'undefined' && value !== null && value !== '');

  return [
    birthday ? `DOB ${birthday}` : '',
    gender,
    formName ? `Form ${formName}` : '',
    houseName ? `House ${houseName}` : '',
    typeof yearGroup !== 'undefined' && yearGroup !== null && yearGroup !== '' ? `Y${yearGroup}` : '',
    item.specialEducationNeeds ? 'SEN' : '',
    item.pupilsPremium ? 'PP' : '',
    item.freeSchoolMeals ? 'FSM' : ''
  ]
    .filter(Boolean)
    .join(' · ');
}

function getTransferItem(
  item: GeneralMessageRecipientPreviewItem,
  recipientType: GeneralMessageRecipientType,
  referenceForms: SchoolForm[],
  referenceHouses: SchoolHouse[]
): GeneralMessageRecipientPreviewItem & { id: string; transferMeta?: string } {
  const forms = propz.get<any[]>(item, ['forms'], []) || [];
  const houses = propz.get<any[]>(item, ['houses'], []) || [];
  const resolvedForm = findReferenceItem(referenceForms, [item.formId, ...(item.formIds || [])]);
  const resolvedHouse = findReferenceItem(referenceHouses, [item.houseId, ...(item.houseIds || [])]);

  return {
    ...item,
    id: getGeneralMessageRecipientPreviewItemKey(item),
    form: propz.get(item, ['form']) || forms[0] || resolvedForm,
    house: propz.get(item, ['house']) || houses[0] || resolvedHouse,
    transferMeta:
      recipientType === 'students' ? getStudentTransferMeta(item, referenceForms, referenceHouses) : undefined
  };
}

function getRecipientInitials(item: GeneralMessageRecipientPreviewItem): string {
  const firstName = item.firstName || '';
  const lastName = item.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim();
  return initials || getRecipientName(item).charAt(0) || '?';
}

function getNameFromObject(item: any): string {
  if (typeof item === 'undefined' || item === null) {
    return '';
  }

  if (typeof item === 'string') {
    return item;
  }

  return item.name || item.title || item.id || '';
}

function getNamesFromList(items: any[]): string {
  return (Array.isArray(items) ? items : [])
    .map(getNameFromObject)
    .filter(name => name !== '')
    .join(', ');
}

function getRecipientDetails(item: GeneralMessageRecipientPreviewItem): string {
  const details = [];

  const forms = getNamesFromList(propz.get(item, ['forms'], []));
  const houses = getNamesFromList(propz.get(item, ['houses'], []));

  if (forms !== '') {
    details.push(forms);
  } else if (item.form && item.form.name) {
    details.push(item.form.name);
  }

  if (houses !== '') {
    details.push(houses);
  } else if (item.house && item.house.name) {
    details.push(item.house.name);
  }

  if (typeof item.yearGroup !== 'undefined') {
    details.push(`Year ${item.yearGroup}`);
  }

  if (item.preset) {
    details.push(item.preset);
  }

  return details.join(' • ');
}

type RecipientPanel = 'available' | 'selected';

function splitChipValues(value: string): string[] {
  return String(value || '')
    .split(/,|\n|•/)
    .map(item => item.trim())
    .filter(item => item !== '');
}

function renderRecipientChips(value: string, type: 'role' | 'sport'): React.ReactNode {
  const values = splitChipValues(value);
  const visibleValues = values.slice(0, 2);
  const moreCount = Math.max(values.length - visibleValues.length, 0);

  if (values.length === 0) {
    return <span className="generalMessageRecipientsSelector__chipEmpty">-</span>;
  }

  return (
    <div className="generalMessageRecipientsSelector__chipCell">
      {visibleValues.map(item => (
        <span
          key={`${type}_${item}`}
          className={`generalMessageRecipientsSelector__chip generalMessageRecipientsSelector__chip--${type}`}
        >
          {item}
        </span>
      ))}
      {moreCount > 0 && <span className="generalMessageRecipientsSelector__chipMore">+{moreCount} more</span>}
    </div>
  );
}
function getColumnStorageKey(user: AppUser, recipientType: GeneralMessageRecipientType, panel: RecipientPanel): string {
  const userId = propz.get(user, ['userId']) || propz.get(user, ['id']) || 'currentUser';
  const schoolId = propz.get(user, ['activeSchoolId']) || propz.get(user, ['activeSchool', 'id']) || 'currentSchool';

  return `generalMessagesRecipientsSelector:${schoolId}:${userId}:${recipientType}:${panel}:hiddenColumns`;
}

function getInitialHiddenFields(
  user: AppUser,
  recipientType: GeneralMessageRecipientType,
  panel: RecipientPanel
): Set<string> {
  try {
    const raw = window.localStorage.getItem(getColumnStorageKey(user, recipientType, panel));
    const fields = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(fields) ? fields : []);
  } catch (error) {
    return new Set();
  }
}

function getColumnValue(item: GeneralMessageRecipientPreviewItem, column: ColumnDefinition): string {
  const accessor: any = (column as any).accessor;
  let value: any = '';

  if (typeof accessor === 'function') {
    value = accessor(item);
  } else if (Array.isArray(accessor)) {
    value = propz.get(item, accessor, '');
  }

  if (Array.isArray(value)) {
    return value
      .map(valueItem => (typeof valueItem === 'object' ? valueItem.name || valueItem.title || valueItem.id : valueItem))
      .join(', ');
  }

  if (typeof value === 'object' && value !== null) {
    return value.name || value.title || value.id || '';
  }

  return typeof value !== 'undefined' && value !== null ? String(value) : '';
}

function getSortOrder(sortColumnsName: string, sortDirection: TABLE_SORT_DIRECTION): string | undefined {
  return sortColumnsName !== '' && sortDirection !== '' ? `${sortColumnsName} ${sortDirection}` : undefined;
}

function applyFormsIntersectionToWhere(where: any, filters: any): any {
  const selectedForms = propz.get(filters, ['forms'], []);

  if (!Array.isArray(selectedForms) || selectedForms.length === 0) {
    return where;
  }

  const currentFormIds = propz.get(where, ['formId', '$in']);

  if (!Array.isArray(currentFormIds) || currentFormIds.length === 0) {
    return where;
  }

  const selectedFormIds = selectedForms.map(formId => String(formId));

  return {
    ...where,
    formId: {
      $in: currentFormIds.map(formId => String(formId)).filter(formId => selectedFormIds.indexOf(formId) !== -1)
    }
  };
}

function getNextSortDirection(
  sortField: string,
  sortColumnsName: string,
  sortDirection: TABLE_SORT_DIRECTION
): TABLE_SORT_DIRECTION {
  if (sortField !== sortColumnsName) {
    return SORT.ASC as TABLE_SORT_DIRECTION;
  }

  if (sortDirection === SORT.ASC) {
    return SORT.DESC as TABLE_SORT_DIRECTION;
  }

  if (sortDirection === SORT.DESC) {
    return '';
  }

  return SORT.ASC as TABLE_SORT_DIRECTION;
}

export const GeneralMessageRecipientsSelector: FunctionComponent<Props> = props => {
  const {
    user,
    recipientType,
    columns,
    selectionGroup,
    onSelectionGroupChange,
    title,
    availableTitle,
    selectedTitle,
    filterOptions,
    serverFieldOptions,
    forms: referenceForms = [],
    houses: referenceHouses = [],
    onHelpClick,
    helpLabel
  } = props;

  const [items, setItems] = useState<GeneralMessageRecipientPreviewItem[]>([]);
  const [selectedPreviewItems, setSelectedPreviewItems] = useState<GeneralMessageRecipientPreviewItem[]>([]);
  const [selectedTotal, setSelectedTotal] = useState<number>(0);
  const [isSelectedLoading, setIsSelectedLoading] = useState<boolean>(false);
  const [selectedErrorMessage, setSelectedErrorMessage] = useState<string>('');
  const [availableSelectedItems, setAvailableSelectedItems] = useState<GeneralMessageRecipientPreviewItem[]>([]);
  const [selectedPanelItems, setSelectedPanelItems] = useState<GeneralMessageRecipientPreviewItem[]>([]);
  const [isSelectAllFilteredPending, setIsSelectAllFilteredPending] = useState<boolean>(false);
  const [pendingFilteredExcludedItems, setPendingFilteredExcludedItems] = useState<
    GeneralMessageRecipientPreviewItem[]
  >([]);
  const [filters, setFilters] = useState<any>(getFilters2(columns, {}));
  const [appliedFilters, setAppliedFilters] = useState<any>(getFilters2(columns, {}));
  const [isShowFilter, setIsShowFilter] = useState<boolean>(false);
  const [isDataFiltered, setIsDataFiltered] = useState<boolean>(false);
  const [availableSearch, setAvailableSearch] = useState<string>('');
  const [appliedAvailableSearch, setAppliedAvailableSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
  const [total, setTotal] = useState<number>(0);

  const [selectedCurrentPage, setSelectedCurrentPage] = useState<number>(FIRST_PAGE);
  const [selectedFilters, setSelectedFilters] = useState<any>(getFilters2(columns, {}));
  const [appliedSelectedFilters, setAppliedSelectedFilters] = useState<any>(getFilters2(columns, {}));
  const [isShowSelectedFilter, setIsShowSelectedFilter] = useState<boolean>(false);
  const [isSelectedDataFiltered, setIsSelectedDataFiltered] = useState<boolean>(false);

  const [sortColumnsName, setSortColumnsName] = useState<string>('lastName');
  const [sortDirection, setSortDirection] = useState<TABLE_SORT_DIRECTION>(SORT.ASC as TABLE_SORT_DIRECTION);

  const [selectedSortColumnsName, setSelectedSortColumnsName] = useState<string>('lastName');
  const [selectedSortDirection, setSelectedSortDirection] = useState<TABLE_SORT_DIRECTION>(
    SORT.ASC as TABLE_SORT_DIRECTION
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const availableRequestId = useRef<number>(0);
  const selectedRequestId = useRef<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [availableHiddenFields, setAvailableHiddenFields] = useState<Set<string>>(() =>
    getInitialHiddenFields(user, recipientType, 'available')
  );
  const [selectedHiddenFields, setSelectedHiddenFields] = useState<Set<string>>(() =>
    getInitialHiddenFields(user, recipientType, 'selected')
  );

  const visibleColumns = useMemo(() => columns.filter(column => !availableHiddenFields.has(column.field)), [
    columns,
    availableHiddenFields
  ]);
  const selectedVisibleColumns = useMemo(() => columns.filter(column => !selectedHiddenFields.has(column.field)), [
    columns,
    selectedHiddenFields
  ]);

  const selectedCount = getGeneralMessageRecipientSelectionGroupSize(selectionGroup);
  const displayedSelectedCount = isSelectedLoading ? selectedCount : selectedTotal;
  const isFilterMode = selectionGroup.mode === GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER;
  const readyToAddCount = isSelectAllFilteredPending
    ? Math.max(total - pendingFilteredExcludedItems.length, 0)
    : availableSelectedItems.length;
  const readyToRemoveCount = selectedPanelItems.length;

  const currentWhere = useMemo(() => {
    const where = applyFormsIntersectionToWhere(
      getServerFieldSectionWhere(appliedFilters, serverFieldOptions),
      appliedFilters
    );
    const search = appliedAvailableSearch.trim();

    return search
      ? {
          ...where,
          $or: [{ firstName: { like: search, options: 'i' } }, { lastName: { like: search, options: 'i' } }]
        }
      : where;
  }, [appliedAvailableSearch, appliedFilters, serverFieldOptions]);
  const currentOrder = useMemo(() => getSortOrder(sortColumnsName, sortDirection), [sortColumnsName, sortDirection]);
  const currentServerFilter = useMemo(() => getServerQueryFilter2(currentPage, currentWhere, currentOrder, LIMIT), [
    currentPage,
    currentWhere,
    currentOrder
  ]);

  const selectedWhere = useMemo(() => {
    const where = getServerFieldSectionWhere(appliedSelectedFilters, serverFieldOptions);
    return applyFormsIntersectionToWhere(where, appliedSelectedFilters);
  }, [appliedSelectedFilters, serverFieldOptions]);

  const selectedOrder = useMemo(() => getSortOrder(selectedSortColumnsName, selectedSortDirection), [
    selectedSortColumnsName,
    selectedSortDirection
  ]);

  const selectedServerFilter = useMemo(
    () => getServerQueryFilter2(selectedCurrentPage, selectedWhere, selectedOrder, LIMIT),
    [selectedCurrentPage, selectedWhere, selectedOrder]
  );

  const selectedRequestParams = useMemo(
    () => ({
      recipientSelection: {
        [recipientType]: selectionGroup
      },
      filter: selectedServerFilter
    }),
    [recipientType, selectionGroup, selectedServerFilter]
  );

  const applyAvailableSearch = (value: string) => {
    setAvailableSearch(value);
    setAppliedAvailableSearch(value);
    setCurrentPage(FIRST_PAGE);
    setAvailableSelectedItems([]);
    setPendingFilteredExcludedItems([]);
    setIsSelectAllFilteredPending(false);
  };

  const loadItems = () => {
    const requestId = ++availableRequestId.current;
    setIsLoading(true);
    setErrorMessage('');

    return searchGeneralMessageRecipients(user, recipientType, currentServerFilter)
      .then(result => {
        if (requestId !== availableRequestId.current) return;
        setTotal(result.count);
        setItems(result.items);
        setAvailableSelectedItems([]);
      })
      .catch(error => {
        if (requestId !== availableRequestId.current) return;
        console.error(error);
        setErrorMessage('Recipients could not be loaded. Please try again.');
      })
      .then(() => {
        if (requestId !== availableRequestId.current) return;
        setIsLoading(false);
      });
  };

  const loadSelectedItems = () => {
    const requestId = ++selectedRequestId.current;
    if (selectedCount === 0) {
      setIsSelectedLoading(false);
      setSelectedTotal(0);
      setSelectedPreviewItems([]);
      setSelectedPanelItems([]);
      setSelectedErrorMessage('');
      return Promise.resolve();
    }

    setIsSelectedLoading(true);
    setSelectedErrorMessage('');

    return searchGeneralMessageSelectedRecipients(user, recipientType, selectedRequestParams)
      .then(result => {
        if (requestId !== selectedRequestId.current) return;
        setSelectedTotal(result.count);
        setSelectedPreviewItems(result.items);
        setSelectedPanelItems([]);
      })
      .catch(error => {
        if (requestId !== selectedRequestId.current) return;
        console.error(error);
        setSelectedErrorMessage('Selected recipients could not be loaded. Please try again.');
      })
      .then(() => {
        if (requestId !== selectedRequestId.current) return;
        setIsSelectedLoading(false);
      });
  };

  useEffect(() => {
    loadItems();
  }, [currentPage, currentWhere, currentOrder, recipientType]);

  useEffect(() => {
    setSelectedCurrentPage(FIRST_PAGE);
  }, [selectionGroup, recipientType, selectedWhere, selectedOrder]);

  useEffect(() => {
    loadSelectedItems();
  }, [selectedCurrentPage, selectedWhere, selectedOrder, recipientType, selectionGroup]);

  function getFilterEventValue(event: any): any {
    if (Array.isArray(event)) {
      return event;
    }

    if (typeof event === 'undefined' || event === null) {
      return '';
    }

    if (typeof event.target !== 'undefined') {
      const selectedOptions = propz.get(event, ['target', 'selectedOptions']);

      if (typeof selectedOptions !== 'undefined') {
        return Array.from(selectedOptions).map((option: any) => option.value);
      }

      return event.target.value;
    }

    return event;
  }

  function normalizeMultiSelectValue(value: any): any[] {
    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'object' && item !== null) {
          return item.value;
        }

        return item;
      });
    }

    if (typeof value === 'object' && value !== null && typeof value.value !== 'undefined') {
      return [value.value];
    }

    if (typeof value === 'undefined' || value === null || value === '') {
      return [];
    }

    return [value];
  }

  function getDateIntervalKey(options: any): 'from' | 'to' | undefined {
    if (options === 'from' || options === DATE_INTERVAL.FROM) {
      return 'from';
    }

    if (options === 'to' || options === DATE_INTERVAL.TO) {
      return 'to';
    }

    return undefined;
  }

  const getNextFilters = (currentFilters: any, event, filterField: string, options?) => {
    const filterValue = getFilterEventValue(event);
    const currentFilterValue = currentFilters[filterField];
    const dateIntervalKey = getDateIntervalKey(options);

    if (typeof dateIntervalKey !== 'undefined') {
      return {
        ...currentFilters,
        [filterField]: {
          ...(typeof currentFilterValue === 'object' && currentFilterValue !== null
            ? currentFilterValue
            : { from: '', to: '' }),
          [dateIntervalKey]: filterValue
        }
      };
    }

    if (Array.isArray(currentFilterValue)) {
      return {
        ...currentFilters,
        [filterField]: normalizeMultiSelectValue(filterValue)
      };
    }

    return {
      ...currentFilters,
      [filterField]: filterValue
    };
  };

  const onFilterChange = (event, filterField: string, options?) => {
    setFilters(getNextFilters(filters, event, filterField, options));
  };

  const onSelectedFilterChange = (event, filterField: string, options?) => {
    setSelectedFilters(getNextFilters(selectedFilters, event, filterField, options));
  };

  const onApplyFilterClick = () => {
    setAppliedFilters(filters);
    setCurrentPage(FIRST_PAGE);
    setIsDataFiltered(isFilterExist2(filters));
    setAvailableSelectedItems([]);
    setPendingFilteredExcludedItems([]);
    setIsSelectAllFilteredPending(false);
  };

  const replaceAndApplyFilters = (nextFilters: any) => {
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(FIRST_PAGE);
    setIsDataFiltered(isFilterExist2(nextFilters));
    setAvailableSelectedItems([]);
    setPendingFilteredExcludedItems([]);
    setIsSelectAllFilteredPending(false);
  };

  const onApplySelectedFilterClick = () => {
    setAppliedSelectedFilters(selectedFilters);
    setSelectedCurrentPage(FIRST_PAGE);
    setIsSelectedDataFiltered(isFilterExist2(selectedFilters));
    setSelectedPanelItems([]);
  };

  const onClearSelectedFilterClick = () => {
    const emptyFilters = getFilters2(columns, {});

    setSelectedFilters(emptyFilters);
    setAppliedSelectedFilters(emptyFilters);
    setIsSelectedDataFiltered(false);
    setSelectedCurrentPage(FIRST_PAGE);
    setSelectedPanelItems([]);
  };

  const onClearFilterClick = () => {
    const emptyFilters = getFilters2(columns, {});

    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setIsDataFiltered(false);
    setCurrentPage(FIRST_PAGE);
    setAvailableSelectedItems([]);
    setSelectedPanelItems([]);
    setPendingFilteredExcludedItems([]);
    setIsSelectAllFilteredPending(false);
  };

  const onSortClick = (sortField: string) => {
    const nextSortDirection = getNextSortDirection(sortField, sortColumnsName, sortDirection);
    setSortColumnsName(nextSortDirection === '' ? '' : sortField);
    setSortDirection(nextSortDirection);
    setCurrentPage(FIRST_PAGE);
  };

  const onSelectedSortClick = (sortField: string) => {
    const nextSortDirection = getNextSortDirection(sortField, selectedSortColumnsName, selectedSortDirection);
    setSelectedSortColumnsName(nextSortDirection === '' ? '' : sortField);
    setSelectedSortDirection(nextSortDirection);
    setSelectedCurrentPage(FIRST_PAGE);
  };

  const toggleAvailableItem = (item: GeneralMessageRecipientPreviewItem) => {
    if (isSelectAllFilteredPending) {
      const itemKey = getGeneralMessageRecipientPreviewItemKey(item);
      const isExcluded = pendingFilteredExcludedItems.some(
        excludedItem => getGeneralMessageRecipientPreviewItemKey(excludedItem) === itemKey
      );
      setPendingFilteredExcludedItems(
        isExcluded
          ? pendingFilteredExcludedItems.filter(
              excludedItem => getGeneralMessageRecipientPreviewItemKey(excludedItem) !== itemKey
            )
          : [...pendingFilteredExcludedItems, item]
      );
      return;
    }

    const selectionItem = getGeneralMessageRecipientSelectionItemFromPreview(item);
    const isSelected = availableSelectedItems.some(selectedItem => {
      const currentSelectionItem = getGeneralMessageRecipientSelectionItemFromPreview(selectedItem);
      return (
        currentSelectionItem.userId === selectionItem.userId &&
        currentSelectionItem.permissionId === selectionItem.permissionId
      );
    });

    setAvailableSelectedItems(
      isSelected
        ? availableSelectedItems.filter(
            selectedItem =>
              getGeneralMessageRecipientPreviewItemKey(selectedItem) !== getGeneralMessageRecipientPreviewItemKey(item)
          )
        : [...availableSelectedItems, item]
    );
  };

  const toggleSelectedPanelItem = (item: GeneralMessageRecipientPreviewItem) => {
    const itemKey = getGeneralMessageRecipientPreviewItemKey(item);
    const isSelected = selectedPanelItems.some(
      selectedItem => getGeneralMessageRecipientPreviewItemKey(selectedItem) === itemKey
    );

    setSelectedPanelItems(
      isSelected
        ? selectedPanelItems.filter(selectedItem => getGeneralMessageRecipientPreviewItemKey(selectedItem) !== itemKey)
        : [...selectedPanelItems, item]
    );
  };

  const isAvailableItemSelectedForTransfer = (item: GeneralMessageRecipientPreviewItem): boolean => {
    const itemKey = getGeneralMessageRecipientPreviewItemKey(item);

    if (isSelectAllFilteredPending) {
      return !pendingFilteredExcludedItems.some(
        excludedItem => getGeneralMessageRecipientPreviewItemKey(excludedItem) === itemKey
      );
    }

    return availableSelectedItems.some(
      selectedItem => getGeneralMessageRecipientPreviewItemKey(selectedItem) === itemKey
    );
  };

  const isSelectedPanelItemSelectedForTransfer = (item: GeneralMessageRecipientPreviewItem): boolean => {
    const itemKey = getGeneralMessageRecipientPreviewItemKey(item);

    return selectedPanelItems.some(selectedItem => getGeneralMessageRecipientPreviewItemKey(selectedItem) === itemKey);
  };

  const isPreviewItem = (item: GeneralMessageRecipientPreviewItem): boolean => {
    const itemKey = getGeneralMessageRecipientPreviewItemKey(item);

    return selectedPreviewItems.some(previewItem => getGeneralMessageRecipientPreviewItemKey(previewItem) === itemKey);
  };

  const getAvailableItemsToRender = (): GeneralMessageRecipientPreviewItem[] => {
    return items.filter(item => {
      if (isPreviewItem(item)) {
        return false;
      }

      if (isGeneralMessageRecipientPreviewItemSelected(item, selectionGroup)) {
        return false;
      }

      return true;
    });
  };

  const pagesCount = Math.ceil(total / LIMIT);

  const selectedPagesCount = Math.ceil(selectedTotal / LIMIT) || 1;
  const selectedPageItems = selectedPreviewItems;

  const isSelectedPrevDisabled = selectedCurrentPage <= FIRST_PAGE;
  const isSelectedNextDisabled = selectedCurrentPage >= selectedPagesCount;

  useEffect(() => {
    if (selectedCurrentPage > selectedPagesCount) {
      setSelectedCurrentPage(selectedPagesCount);
    }
  }, [selectedCurrentPage, selectedPagesCount]);

  const moveToNextPageIfCurrentPageIsEmpty = (itemsMoved: GeneralMessageRecipientPreviewItem[]) => {
    const movedKeys = itemsMoved.map(getGeneralMessageRecipientPreviewItemKey);

    const remainingItemsOnCurrentPage = getAvailableItemsToRender().filter(
      item => movedKeys.indexOf(getGeneralMessageRecipientPreviewItemKey(item)) === -1
    );

    if (remainingItemsOnCurrentPage.length === 0 && currentPage < pagesCount) {
      setCurrentPage(currentPage + 1);
    }
  };

  const addItemsToSelection = (itemsToAdd: GeneralMessageRecipientPreviewItem[]) => {
    if (itemsToAdd.length === 0) {
      return;
    }

    const nextGroup = selectGeneralMessageRecipientPageItems(selectionGroup, itemsToAdd);

    onSelectionGroupChange({ ...nextGroup, previewItems: [] });
    setAvailableSelectedItems([]);
    setPendingFilteredExcludedItems([]);
    setSelectedPanelItems([]);
    setIsSelectAllFilteredPending(false);

    moveToNextPageIfCurrentPageIsEmpty(itemsToAdd);
  };

  const removeItemsFromSelection = (itemsToRemove: GeneralMessageRecipientPreviewItem[]) => {
    if (itemsToRemove.length === 0) {
      return;
    }

    const nextGroup = unselectGeneralMessageRecipientPageItems(selectionGroup, itemsToRemove);

    onSelectionGroupChange({ ...nextGroup, previewItems: [] });
    setSelectedPanelItems([]);
    setAvailableSelectedItems([]);
    setSelectedCurrentPage(FIRST_PAGE);
  };

  const addSelectedAvailableItems = () => {
    if (isSelectAllFilteredPending) {
      const nextGroup = selectAllFilteredGeneralMessageRecipients(
        selectionGroup,
        { where: currentWhere, order: currentOrder },
        total
      );
      nextGroup.excluded = addGeneralMessageRecipientSelectionItems(
        nextGroup.excluded || [],
        pendingFilteredExcludedItems.map(getGeneralMessageRecipientSelectionItemFromPreview)
      );
      onSelectionGroupChange({ ...nextGroup, previewItems: [] });
      setAvailableSelectedItems([]);
      setPendingFilteredExcludedItems([]);
      setSelectedPanelItems([]);
      setSelectedCurrentPage(FIRST_PAGE);
      setIsSelectAllFilteredPending(false);
      return;
    }

    addItemsToSelection(availableSelectedItems);
  };

  const removeSelectedRecipients = () => {
    removeItemsFromSelection(selectedPanelItems);
  };

  const selectCurrentPage = () => {
    setAvailableSelectedItems(getAvailableItemsToRender());
    setSelectedPanelItems([]);
    setPendingFilteredExcludedItems([]);
    setIsSelectAllFilteredPending(false);
  };

  const selectAllFiltered = () => {
    setAvailableSelectedItems([]);
    setPendingFilteredExcludedItems([]);
    setSelectedPanelItems([]);
    setIsSelectAllFilteredPending(total > 0);
  };

  const selectAllSelectedRecipients = () => {
    setIsSelectedLoading(true);
    setSelectedErrorMessage('');
    getGeneralMessageSelectedRecipientsPreview(user, recipientType, {
      ...selectedRequestParams,
      filter: { ...selectedServerFilter, skip: 0, limit: Math.max(selectedTotal, LIMIT) }
    })
      .then(allItems => {
        setSelectedPanelItems(allItems);
        setIsSelectedLoading(false);
      })
      .catch(error => {
        console.error(error);
        setSelectedErrorMessage('Could not load all selected recipients. Please try again.');
        setIsSelectedLoading(false);
      });
  };

  const clearSelection = () => {
    onSelectionGroupChange({
      mode: GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.MANUAL,
      all: false,
      selected: [],
      excluded: [],
      filter: {},
      matchingCount: 0,
      previewItems: []
    });
    setSelectedPreviewItems([]);
    setSelectedTotal(0);
    setSelectedPanelItems([]);
    setAvailableSelectedItems([]);
    setPendingFilteredExcludedItems([]);
    setIsSelectAllFilteredPending(false);
  };

  const unselectCurrentPage = () => {
    removeItemsFromSelection(items);
  };

  const toggleColumn = (
    field: string,
    panel: RecipientPanel,
    setHiddenFields: React.Dispatch<React.SetStateAction<Set<string>>>
  ) => {
    setHiddenFields(currentHiddenFields => {
      const nextHiddenFields = new Set(currentHiddenFields);

      if (nextHiddenFields.has(field)) {
        nextHiddenFields.delete(field);
      } else {
        nextHiddenFields.add(field);
      }

      try {
        window.localStorage.setItem(
          getColumnStorageKey(user, recipientType, panel),
          JSON.stringify(Array.from(nextHiddenFields))
        );
      } catch (error) {
        console.error(error);
      }

      return nextHiddenFields;
    });
  };

  const isColumnToggleable = (field: string): boolean => field !== 'firstName' && field !== 'lastName';

  const renderHeaderCell = (column: ColumnDefinition) => {
    if (column.isSort) {
      const isCurrentSort = sortColumnsName === column.field && sortDirection !== '';
      return (
        <th key={column.field}>
          <button
            type="button"
            className="generalMessageRecipientsSelector__sortBtn"
            onClick={() => onSortClick(column.field)}
          >
            {column.text}
            {isCurrentSort && <span className="generalMessageRecipientsSelector__sortDirection"> {sortDirection}</span>}
          </button>
        </th>
      );
    }

    return <th key={column.field}>{column.text}</th>;
  };

  const renderSelectedHeaderCell = (column: ColumnDefinition) => {
    if (column.isSort) {
      const isCurrentSort = selectedSortColumnsName === column.field && selectedSortDirection !== '';

      return (
        <th key={column.field}>
          <button
            type="button"
            className="generalMessageRecipientsSelector__sortBtn"
            onClick={() => onSelectedSortClick(column.field)}
          >
            {column.text}
            {isCurrentSort && (
              <span className="generalMessageRecipientsSelector__sortDirection"> {selectedSortDirection}</span>
            )}
          </button>
        </th>
      );
    }

    return <th key={column.field}>{column.text}</th>;
  };

  const renderRecipientRow = (
    item: GeneralMessageRecipientPreviewItem,
    rowColumns: ColumnDefinition[],
    isChecked: boolean,
    onToggle: (item: GeneralMessageRecipientPreviewItem) => void,
    onDoubleClick: (items: GeneralMessageRecipientPreviewItem[]) => void,
    isSoftSelected?: boolean
  ) => {
    const details = getRecipientDetails(item);

    return (
      <tr
        key={getGeneralMessageRecipientPreviewItemKey(item)}
        className={
          isSoftSelected
            ? 'generalMessageRecipientsSelector__row is-soft-selected'
            : 'generalMessageRecipientsSelector__row'
        }
        onClick={() => onToggle(item)}
        onDoubleClick={() => onDoubleClick([item])}
      >
        <td className="generalMessageRecipientsSelector__checkboxCell">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={event => {
              event.stopPropagation();
              onToggle(item);
            }}
            onClick={event => event.stopPropagation()}
          />
        </td>
        {rowColumns.map(column => {
          const value = getColumnValue(item, column);

          if (column.field === 'firstName') {
            return (
              <td key={column.field}>
                <div className="generalMessageRecipientsSelector__identity">
                  <span className="generalMessageRecipientsSelector__identityText">
                    <span className="generalMessageRecipientsSelector__name">{getRecipientName(item)}</span>
                    {details !== '' && <span className="generalMessageRecipientsSelector__details">{details}</span>}
                  </span>
                </div>
              </td>
            );
          }

          const isRoleColumn = column.field === 'role';
          const isSportColumn = column.field === 'permissionSportIds';

          return (
            <td key={column.field}>
              {isRoleColumn || isSportColumn ? (
                renderRecipientChips(value, isRoleColumn ? 'role' : 'sport')
              ) : (
                <div>{value}</div>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  const availableItemsToRender = getAvailableItemsToRender();

  const renderAvailableRows = availableItemsToRender.map(item => {
    const selectedForTransfer = isAvailableItemSelectedForTransfer(item);

    const isChecked = selectedForTransfer;
    const isSoftSelected = selectedForTransfer;

    return renderRecipientRow(
      item,
      visibleColumns,
      isChecked,
      toggleAvailableItem,
      addItemsToSelection,
      isSoftSelected
    );
  });

  const renderSelectedRows = selectedPageItems.map(item => {
    const selectedForTransfer = isSelectedPanelItemSelectedForTransfer(item);

    return renderRecipientRow(
      item,
      selectedVisibleColumns,
      selectedForTransfer,
      toggleSelectedPanelItem,
      removeItemsFromSelection,
      selectedForTransfer
    );
  });

  const isPrevDisabled = currentPage <= FIRST_PAGE;
  const isNextDisabled = currentPage >= pagesCount;

  const availableTransferItems = availableItemsToRender.map(item =>
    getTransferItem(item, recipientType, referenceForms, referenceHouses)
  );
  const selectedTransferItems = selectedPageItems.map(item =>
    getTransferItem(item, recipientType, referenceForms, referenceHouses)
  );
  const availableTransferSelection = availableItemsToRender
    .filter(isAvailableItemSelectedForTransfer)
    .map(item => getTransferItem(item, recipientType, referenceForms, referenceHouses));
  const selectedTransferSelection = selectedPanelItems.map(item =>
    getTransferItem(item, recipientType, referenceForms, referenceHouses)
  );
  const isSearchPending = availableSearch !== appliedAvailableSearch;

  return (
    <div className="generalMessageRecipientsSelector generalMessageRecipientsSelector--handoff twoPanelEditor twoPanelEditor--sourceFirst">
      {title && <h3 className="generalMessageRecipientsSelector__title">{title}</h3>}
      {errorMessage !== '' && <div className="alert alert-danger">{errorMessage}</div>}
      {selectedErrorMessage !== '' && <div className="alert alert-danger">{selectedErrorMessage}</div>}

      <div className="generalMessageRecipientsSelector__summary">
        <div className="generalMessageRecipientsSelector__summaryMain">
          <span className="generalMessageRecipientsSelector__summaryPill">Selected: {displayedSelectedCount}</span>
          {readyToAddCount > 0 && (
            <span className="generalMessageRecipientsSelector__summaryHint">
              {isSelectAllFilteredPending ? `All ${readyToAddCount} matching` : readyToAddCount} checked — move them
              when you are ready.
            </span>
          )}
          {isFilterMode && (
            <span className="generalMessageRecipientsSelector__summaryHint">
              All recipients matching your saved selections are included.
            </span>
          )}
        </div>
        {onHelpClick && <HelpIconButton onClick={onHelpClick} label={helpLabel || 'How to select recipients'} />}
      </div>

      <div className="generalMessageRecipientsSelector__layout">
        <Panel
          isSourceFirst
          showAvatars={false}
          alwaysShowSelectAllLink
          unitLabel="recipients"
          items={availableTransferItems}
          itemsSelected={availableTransferSelection}
          title={availableTitle || 'Available recipients'}
          columns={visibleColumns}
          onItemClick={(index, item) => toggleAvailableItem(item || availableItemsToRender[index])}
          onItemDoubleClick={(index, item) => addItemsToSelection([item || availableItemsToRender[index]])}
          filters={filters}
          filterOptions={filterOptions}
          filterLabels={
            recipientType === 'students'
              ? { ages: 'Year groups', houses: 'Houses', forms: 'Reg forms', gender: 'Gender', tag: 'Tags' }
              : recipientType === 'staffs'
              ? { permissionHouseIds: 'Houses', permissionFormIds: 'Reg forms', tag: 'Tags' }
              : { tag: 'Tags' }
          }
          useRecipientFilterLayout
          isShowFilter={isShowFilter}
          isFiltered={isDataFiltered}
          onFilterClick={() => setIsShowFilter(!isShowFilter)}
          onFilterChange={onFilterChange}
          onApplyFilterClick={onApplyFilterClick}
          onClearFilterClick={onClearFilterClick}
          onReplaceFilters={replaceAndApplyFilters}
          searchValue={availableSearch}
          onSearchChange={applyAvailableSearch}
          total={total}
          currentPage={currentPage}
          setCurrentPageParams={setCurrentPage}
          onSelectAllClick={selectAllFiltered}
          onSelectAllOnPageClick={selectCurrentPage}
          onUnselectAllClick={() => {
            setAvailableSelectedItems([]);
            setPendingFilteredExcludedItems([]);
            setIsSelectAllFilteredPending(false);
          }}
          onSelectAllFilteredClick={selectAllFiltered}
          isSelectAllChecked={
            availableTransferItems.length > 0 && availableTransferSelection.length === availableTransferItems.length
          }
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          onSortClick={onSortClick}
          itemsAddBuffer={[]}
          itemsRemoveBuffer={[]}
          isLoading={isLoading || isSearchPending}
        />

        <div className="twoPanelEditor__actionsCol">
          <div className="twoPanelEditor__actions">
            <button
              type="button"
              className="twoPanelEditor__moveBtn"
              onClick={addSelectedAvailableItems}
              disabled={readyToAddCount === 0}
              title={isSelectAllFilteredPending ? 'Add all matching recipients' : 'Add selected'}
              aria-label="Add checked recipients"
            >
              <span aria-hidden="true">›</span>
            </button>
            <button
              type="button"
              className="twoPanelEditor__moveBtn"
              onClick={removeSelectedRecipients}
              disabled={readyToRemoveCount === 0}
              title="Remove selected"
              aria-label="Remove checked recipients"
            >
              <span aria-hidden="true">‹</span>
            </button>
          </div>
        </div>

        <div className="generalMessageRecipientsSelector__selectedPanel">
          <Panel
            isSourceFirst
            isNoVisibleBlock
            showAvatars={false}
            unitLabel="recipients"
            items={selectedTransferItems}
            itemsSelected={selectedTransferSelection}
            title={selectedTitle || 'Selected recipients'}
            columns={selectedVisibleColumns}
            onItemClick={(index, item) => toggleSelectedPanelItem(item || selectedPageItems[index])}
            onItemDoubleClick={(index, item) => removeItemsFromSelection([item || selectedPageItems[index]])}
            searchExtra={
              selectedCount > 0 ? (
                <button type="button" className="generalMessageRecipientsSelector__clearAll" onClick={clearSelection}>
                  Clear selected
                </button>
              ) : (
                undefined
              )
            }
            filters={selectedFilters}
            filterOptions={filterOptions}
            isShowFilter={false}
            isFiltered={isSelectedDataFiltered}
            onFilterClick={() => undefined}
            onFilterChange={onSelectedFilterChange}
            onApplyFilterClick={onApplySelectedFilterClick}
            onClearFilterClick={onClearSelectedFilterClick}
            total={selectedTotal}
            currentPage={selectedCurrentPage}
            setCurrentPageParams={setSelectedCurrentPage}
            onSelectAllClick={selectAllSelectedRecipients}
            onSelectAllOnPageClick={() => setSelectedPanelItems(selectedPageItems)}
            onUnselectAllClick={() => setSelectedPanelItems([])}
            isSelectAllChecked={
              selectedTransferItems.length > 0 && selectedTransferSelection.length === selectedTransferItems.length
            }
            sortDirection={selectedSortDirection}
            sortColumnsName={selectedSortColumnsName}
            onSortClick={onSelectedSortClick}
            itemsAddBuffer={[]}
            itemsRemoveBuffer={[]}
            isLoading={isSelectedLoading}
          />
        </div>
      </div>
    </div>
  );
};

GeneralMessageRecipientsSelector.displayName = 'GeneralMessageRecipientsSelector';
